import { FilterTypes } from '../../../../interfaces/src/lib/enums/index';
import {
  setPortOfCall,
  setPriceCode,
  setPriceType,
  setSelectedAirports,
  setSelectedCabins,
  setSelectedCategories,
  setSelectedCruiseId,
  setSelectedDestinations,
  setSelectedDestinationsExcursions,
  setSelectedPorts,
  setSelectedPortsExcursions,
  setSelectedShips,
  setIsAirportToggleEnabled,
} from '../stateManager/slices/facetFilterSlice';
import {
  getDealsFilters,
  getDealsFiltersEditable,
  getDealsFiltersUserCleared,
  getDealsPromotions,
} from './contextAwareUtils';
import { AppDispatch, store } from '../stateManager/store';
import {
  setFlyAndCruise,
  setHasAir,
  setNumberOfNights,
  setPromotions,
  setTags,
} from '../stateManager/slices/genericFilterSlice';
import { FilterOptionSliceType } from '../../../../interfaces/src/lib/types/index';
import { setFirstCabinState, setSecondCabinState } from '../stateManager/slices/guestFilterSlice';
import { splitNightsRangeOnFilters } from './utilFuncs';

// Define the interface for DealsFilter
interface DealsFilter {
  filterType: FilterTypes;
  filterValue: string;
}

/**
 * Processes and dispatches various filters for deals.
 *
 * @param {AppDispatch} dispatch - The dispatch function from the Redux store.
 *
 * This function retrieves the deals filters from the Redux store and applies them
 * by dispatching appropriate settings. It handles various filter types such as ship,
 * port, number of nights, cabin, cruise ID, price type, price code, port of call,
 * destination, fly and cruise, start date, end date, min price, max price, and guest filters.
 *
 * The function also collects complex filter data (dates, prices, and guest data) and
 * dispatches them after the loop.
 */
export function useDealsFilters(dispatch: AppDispatch) {
  // Get the raw filters from the deals context
  const rawFilters = getDealsFilters();
  const promotions = getDealsPromotions();

  const state = store.getState();
  const selectedDestinations = state.facetFilter.selectedDestinations;
  const selectedDestinationsExcursions = state.facetFilter.selectedDestinationsExcursions;
  const selectedCategories = state.facetFilter.selectedCategories;
  const selectedShips = state.facetFilter.selectedShips;
  const selectedPorts = state.facetFilter.selectedPorts;
  const selectedPortsExcursions = state.facetFilter.selectedPortsExcursions;
  const selectedCabins = state.facetFilter.selectedCabins;
  const selectedAirports = state.facetFilter.selectedAirports;
  const selectedNumberOfNights = state.genericFilter.numberOfNights;
  const selectedTags = state.genericFilter.tag;
  const selectedFlyAndCruise = state.genericFilter.flyAndCruise;
  const selectedGuests = state.guestFilter;
  const cruiseId = state.facetFilter.selectedCruiseId;
  const portOfCall = state.facetFilter.selectedPortOfCall;
  const priceCode = state.facetFilter.selectedPriceCode;
  const allPriceTypes: FilterOptionSliceType[] = [];

  if (!Array.isArray(rawFilters) || rawFilters.length === 0) return;

  // Create a set of valid filterType values from the FilterTypes enum
  const validFilterTypes = new Set<string>(Object.values(FilterTypes));

  // Map rawFilters to ensure filterType is of type FilterTypes
  const filters: DealsFilter[] = rawFilters
    .map((filter) => {
      const filterTypeValue = filter.filterType;
      if (!validFilterTypes.has(filterTypeValue)) {
        // Handle invalid filterType
        console.warn(`Invalid filterType: ${filter.filterType}`);
        return null;
      }

      // Cast filterType to FilterTypes
      const filterType = filterTypeValue as FilterTypes;
      return {
        filterType,
        filterValue: filter.filterValue,
      };
    })
    .filter((filter): filter is DealsFilter => filter !== null);

  /* OBJECTS TO COLLECT COMPLEX FILTERS DATA START */

  const guestData = {
    numberOfAdults: selectedGuests?.firstCabin.adults,
    numberOfMinors: selectedGuests?.firstCabin.minors,
    numberOfJuniors: selectedGuests?.firstCabin.juniors,
    firstCabinAccessibility: selectedGuests?.firstCabin.isAccessible,
    cabin2Adults: 0,
    cabin2Minors: 0,
    cabin2Juniors: 0,
    secondCabinAccessibility: false,
    isConnectedCabin: false,
  };

  /* OBJECTS TO COLLECT COMPLEX FILTERS DATA END */

  // Map of handlers for each filter type
  const filterHandlers: {
    [key in FilterTypes]?: (filterValue: string) => void;
  } = {
    [FilterTypes.SHIP]: (filterValue: string) => {
      // if selected ship is not empty is not necessary to set it again
      if (selectedShips.length === 0)
        dispatch(setSelectedShips(parseValues(FilterTypes.SHIP, filterValue)));
    },
    [FilterTypes.PORT]: (filterValue: string) => {
      if (selectedPorts.length === 0)
        dispatch(setSelectedPorts(parseValues(FilterTypes.PORT, filterValue)));
    },
    [FilterTypes.PORT_EXCURSIONS]: (filterValue: string) => {
      if (selectedPortsExcursions.length === 0)
        dispatch(setSelectedPortsExcursions(parseValues(FilterTypes.PORT_EXCURSIONS, filterValue)));
    },
    [FilterTypes.NUMBER_OF_NIGHTS]: (filterValue: string) => {
      let newFilterValue = filterValue;
      if (filterValue.includes('-')) {
        // if filter value is a range of numbers
        newFilterValue = splitNightsRangeOnFilters(filterValue);
      }
      if (selectedNumberOfNights.length === 0)
        dispatch(
          setNumberOfNights([{ name: FilterTypes.NUMBER_OF_NIGHTS, value: newFilterValue }])
        );
    },
    [FilterTypes.CABIN]: (filterValue: string) => {
      if (selectedCabins.length === 0)
        dispatch(setSelectedCabins(parseValues(FilterTypes.CABIN, filterValue)));
    },
    [FilterTypes.AIRPORT]: (filterValue: string) => {
      if (selectedAirports.length === 0) {
        dispatch(setSelectedAirports(parseValues(FilterTypes.AIRPORT, filterValue)));
        dispatch(setIsAirportToggleEnabled(true));
      }
    },
    [FilterTypes.CRUISE_ID]: (filterValue: string) => {
      if (cruiseId.length === 0)
        dispatch(setSelectedCruiseId(parseValues(FilterTypes.CRUISE_ID, filterValue)));
    },
    [FilterTypes.PRICE_TYPE]: (filterValue: string) => {
      const newPriceTypes = parseValues(FilterTypes.PRICE_TYPE, filterValue);
      const updatedPriceTypes = [...allPriceTypes, ...newPriceTypes];
      const foundPriceType =
        newPriceTypes?.length &&
        allPriceTypes.find((priceType) => priceType.value === newPriceTypes[0].value);
      if (!foundPriceType) {
        allPriceTypes.push(newPriceTypes[0]);
      }
      dispatch(setPriceType(updatedPriceTypes));
    },
    [FilterTypes.PRICE_CODE]: (filterValue: string) => {
      if (priceCode.length === 0)
        dispatch(setPriceCode(parseValues(FilterTypes.PRICE_CODE, filterValue)));
    },
    [FilterTypes.PORT_OF_CALL]: (filterValue: string) => {
      if (portOfCall.length === 0)
        dispatch(setPortOfCall(parseValues(FilterTypes.PORT_OF_CALL, filterValue)));
    },
    [FilterTypes.CATEGORY]: (filterValue: string) => {
      if (selectedCategories.length === 0)
        dispatch(setSelectedCategories(parseValues(FilterTypes.CATEGORY, filterValue)));
    },
    [FilterTypes.DESTINATION]: (filterValue: string) => {
      if (selectedDestinations.length === 0)
        dispatch(setSelectedDestinations(parseValues(FilterTypes.DESTINATION, filterValue)));
    },
    [FilterTypes.DESTINATION_EXCURSIONS]: (filterValue: string) => {
      if (selectedDestinationsExcursions.length === 0)
        dispatch(
          setSelectedDestinationsExcursions(
            parseValues(FilterTypes.DESTINATION_EXCURSIONS, filterValue)
          )
        );
    },
    [FilterTypes.FLY_AND_CRUISE]: (filterValue: string) => {
      const filterValueFly = filterValue === 'true' || filterValue === 'hasAir' ? 'hasAir' : '';
      if (selectedFlyAndCruise.length === 0) {
        dispatch(setHasAir(filterValueFly === 'hasAir'));
        dispatch(setFlyAndCruise([{ name: FilterTypes.FLY_AND_CRUISE, value: filterValueFly }]));
      }
    },
    [FilterTypes.TAG]: (filterValue: string) => {
      if (selectedTags.length === 0) dispatch(setTags(parseValues(FilterTypes.TAG, filterValue)));
    },

    /* COLLECT DATA AND HANDLE AFTER THE LOOP START */

    // Guest filters
    [FilterTypes.ADULTS]: (filterValue: string) => {
      guestData.numberOfAdults = parseInt(filterValue, 10);
    },
    [FilterTypes.MINORS]: (filterValue: string) => {
      guestData.numberOfMinors = parseInt(filterValue, 10);
    },
    [FilterTypes.JUNIORS]: (filterValue: string) => {
      guestData.numberOfJuniors = parseInt(filterValue, 10);
    },
    [FilterTypes.FIRST_CABIN_ACCESSIBILITY]: (filterValue: string) => {
      guestData.firstCabinAccessibility = filterValue === 'true';
    },
    [FilterTypes.SECOND_CABIN_ACCESSIBILITY]: (filterValue: string) => {
      if (!selectedGuests?.secondCabin?.isAccessible)
        guestData.secondCabinAccessibility = filterValue === 'true';
    },
    [FilterTypes.CABIN2_ADULTS]: (filterValue: string) => {
      guestData.cabin2Adults = parseInt(filterValue, 10);
    },
    [FilterTypes.CABIN2_MINORS]: (filterValue: string) => {
      if (!selectedGuests?.secondCabin?.minors) guestData.cabin2Minors = parseInt(filterValue, 10);
    },
    [FilterTypes.CABIN2_JUNIORS]: (filterValue: string) => {
      if (!selectedGuests?.secondCabin?.juniors)
        guestData.cabin2Juniors = parseInt(filterValue, 10);
    },
    [FilterTypes.IS_CONNECTED_CABIN]: (filterValue: string) => {
      if (!selectedGuests?.secondCabin?.isConnectedCabin)
        guestData.isConnectedCabin = filterValue === 'true';
    },
  };

  // On static SEO pages, skip re-applying filters the user has explicitly cleared
  const userCleared = getDealsFiltersEditable() ? getDealsFiltersUserCleared() : [];

  // Iterate through filters and apply handlers
  filters.forEach((dealsFilter: DealsFilter) => {
    if (userCleared.includes(dealsFilter.filterType)) return;
    const handler = filterHandlers[dealsFilter.filterType];
    if (handler) {
      handler(dealsFilter.filterValue);
    }
  });

  // Handle guest filters after collecting data
  const {
    numberOfAdults,
    numberOfMinors,
    numberOfJuniors,
    firstCabinAccessibility,
    cabin2Adults,
    cabin2Minors,
    cabin2Juniors,
    secondCabinAccessibility,
    isConnectedCabin,
  } = guestData;

  if (!promotions?.minimumOccupancyRule || !promotions?.maximumOccupancyRule) {
    // Set first cabin state if applicable
    if (
      numberOfAdults > 0 ||
      numberOfMinors > 0 ||
      numberOfJuniors > 0 ||
      firstCabinAccessibility
    ) {
      dispatch(
        setFirstCabinState({
          isActive: true,
          adults: numberOfAdults,
          minors: numberOfMinors,
          juniors: numberOfJuniors,
          isAccessible: firstCabinAccessibility,
          useInSearch: true,
        })
      );
    }
  }

  // Set second cabin state if applicable
  if (cabin2Adults > 0 || cabin2Minors > 0 || cabin2Juniors > 0 || secondCabinAccessibility) {
    dispatch(
      setSecondCabinState({
        isActive: true,
        adults: cabin2Adults,
        minors: cabin2Minors,
        juniors: cabin2Juniors,
        isAccessible: secondCabinAccessibility,
        isConnectedCabin: isConnectedCabin,
        useInSearch: true,
      })
    );
  }
  /* HANDLE COMPLEX FILTERS END */
}

// Function to set promotions from deals
export const useDealsPromotions = (dispatch: AppDispatch) => {
  const promotion = getDealsPromotions();
  const state = store.getState();
  const selectedPromotions = state.genericFilter.promotion;
  // Set the promotion if available
  if (promotion?.promotionName && promotion?.id) {
    const storedValuesTransformed: FilterOptionSliceType[] = [
      {
        name: promotion.promotionName,
        value: promotion.id,
        useInSearch: true,
      },
    ];
    if (selectedPromotions.length === 0) dispatch(setPromotions(storedValuesTransformed));
  }

  // Set occupancy if available
  if (promotion?.minimumOccupancyRule) {
    dispatch(
      setFirstCabinState({
        isActive: true,
        adults: parseInt(getMinimumOccupancyForGuest(0)),
        minors: parseInt(getMinimumOccupancyForGuest(1)),
        juniors: parseInt(getMinimumOccupancyForGuest(2)),
        isAccessible: false,
        useInSearch: true,
      })
    );
  }
};

// Function to check if a filter type is included in deals filters (and thus non-removable).
// Returns false when dealsFiltersEditable is true (static SEO pages) so filters stay editable.
export function checkDealsFilterType(filterType: string): boolean {
  if (getDealsFiltersEditable()) return false;
  const rawFilters = getDealsFilters();
  if (!Array.isArray(rawFilters) || rawFilters.length === 0 || !filterType) return false;
  // Create a set of valid filterType values
  const validFilterTypes = new Set<string>(Object.values(FilterTypes));

  // Check if the filterType is valid
  if (!validFilterTypes.has(filterType)) {
    return false;
  }

  return rawFilters.some((filter) => filter.filterType === filterType);
}

// Function to check if a preset airport code exists in deals filters
export function checkPresetAirportCode(code: string): boolean {
  // If the airport filter type is not present, return false
  if (!checkDealsFilterType(FilterTypes.AIRPORT)) return false;
  const rawFilters = getDealsFilters();
  const airportFilters = rawFilters.filter((filter) => filter.filterType === FilterTypes.AIRPORT);
  const isPreset = airportFilters.some((filter) => filter.filterValue);
  return isPreset;
}

// Function to check if there is a guest filter in deals filters
export function checkIsDealsGuestFilter(): boolean {
  const rawFilters = getDealsFilters();
  if (!Array.isArray(rawFilters) || rawFilters.length === 0) return false;

  const guestFilterTypes = new Set<string>([
    FilterTypes.ADULTS,
    FilterTypes.MINORS,
    FilterTypes.JUNIORS,
    FilterTypes.FIRST_CABIN_ACCESSIBILITY,
  ]);

  return rawFilters.some((filter) => guestFilterTypes.has(filter.filterType));
}

export function checkIsDealPromotionGuestFilter(): boolean {
  const promoData = getDealsPromotions();
  const minOccupancyRule = promoData?.minimumOccupancyRule?.split('|');
  const maximumOccupancyRule = promoData?.maximumOccupancyRule?.split('|');

  return minOccupancyRule !== undefined || maximumOccupancyRule !== undefined;
}

export function getMinimumOccpancyRule(): string {
  const promoData = getDealsPromotions();
  return promoData?.minimumOccupancyRule || '';
}

export function getMaximumOccupancuRule(): string {
  const promoData = getDealsPromotions();
  return promoData?.maximumOccupancyRule || '';
}

export function getMinimumOccupancyForGuest(index: number) {
  const minOccupancyRule = getMinimumOccpancyRule().split('|');
  return minOccupancyRule[index];
}

export function getMaximumOccupancyForGuest(index: number) {
  const maxOccupancyRule = getMaximumOccupancuRule().split('|');
  return maxOccupancyRule[index];
}

// Function to check if there is a second cabin in deals filters
export function checkIsDealsSecondCabin(): boolean {
  const rawFilters = getDealsFilters();
  if (!Array.isArray(rawFilters) || rawFilters.length === 0) return false;

  const secondCabinFilterTypes = new Set<string>([
    FilterTypes.CABIN2_ADULTS,
    FilterTypes.CABIN2_MINORS,
    FilterTypes.CABIN2_JUNIORS,
    FilterTypes.IS_CONNECTED_CABIN,
    FilterTypes.SECOND_CABIN_ACCESSIBILITY,
  ]);

  return rawFilters.some((filter) => secondCabinFilterTypes.has(filter.filterType));
}

// Function to check if a promotion is included in deals promotions
export function checkDealsPromotion(): boolean {
  const promotion = getDealsPromotions();
  return promotion?.id != null;
}

export function isDealsPage() {
  return getDealsFilters().length > 0 || checkDealsPromotion();
}

// const parseValues = (_name: string | null | undefined, paramValue: string | null | undefined) => {
//   if (!paramValue) return [];
//   return paramValue.split(',').map((item) => ({ name: '', value: item.trim() }));
// };
const parseValues = (_name: string | null | undefined, paramValue: any) => {
  if (!paramValue) return [];

  let values: string[] = [];

  if (Array.isArray(paramValue)) {
    values = paramValue;
  } else if (typeof paramValue === 'object') {
    values = Object.values(paramValue);
  } else if (typeof paramValue === 'string') {
    values = paramValue.split(',');
  }

  return values.filter(Boolean).map((item) => ({ name: '', value: String(item).trim() }));
};

// Function to check if promotion filters are filled
export function checkPromotionFilters(): {
  hasDateFilter: boolean;
  hasOccupancyFilter: boolean;
  hasPromotion: boolean;
  hasPriceRangeFilter: boolean;
} {
  const promotions = getDealsPromotions();
  const rawFilters = getDealsFilters();

  const hasDateFilter =
    promotions?.startDate != null ||
    promotions?.endDate != null ||
    rawFilters.some((filter) => {
      return (
        filter.filterType === FilterTypes.START_DATE || filter.filterType === FilterTypes.END_DATE
      );
    });

  const hasPriceRangeFilter = rawFilters.some((filter) => {
    return (
      filter.filterType === FilterTypes.MIN_PRICE || filter.filterType === FilterTypes.MAX_PRICE
    );
  });

  const hasOccupancyFilter = rawFilters.some((filter) => {
    return (
      filter.filterType === FilterTypes.ADULTS ||
      filter.filterType === FilterTypes.MINORS ||
      filter.filterType === FilterTypes.JUNIORS ||
      filter.filterType === FilterTypes.FIRST_CABIN_ACCESSIBILITY ||
      filter.filterType === FilterTypes.SECOND_CABIN_ACCESSIBILITY ||
      filter.filterType === FilterTypes.CABIN2_ADULTS ||
      filter.filterType === FilterTypes.CABIN2_MINORS ||
      filter.filterType === FilterTypes.CABIN2_JUNIORS ||
      filter.filterType === FilterTypes.IS_CONNECTED_CABIN
    );
  });

  const hasPromotion = promotions?.id != null;

  return {
    hasDateFilter,
    hasOccupancyFilter,
    hasPromotion,
    hasPriceRangeFilter,
  };
}

export function isBehindScenes(): boolean {
  const rawFilters = getDealsFilters();
  return rawFilters?.some(
    (item) => item.filterType === 'category' && item.filterValue === 'BEHINDTHESCENES'
  );
}
